/**
 * ONE ASYNC READ, WITH ITS THREE STATES KEPT APART.
 *
 * ── WHY THIS EXISTS ──
 * The audit found the same defect on Buildings, Users, Tenants, Billing,
 * Devices and the resident portal: a `useEffect` that fetches, no `catch`, no
 * loading flag, and a render whose only branch for "the array is empty" is the
 * empty state. So a failed request is presented to the user as a confident
 * statement about their own data —
 *
 *     an owner with forty buildings is told "No properties yet",
 *     and the only affordance offered is "Add your first building",
 *
 * which spends prepaid units re-creating buildings that already exist. On the
 * Devices screen the same shape says "No other active sessions." during a
 * failure, which is a security-relevant lie.
 *
 * NEVER-HAD-DATA, FAILED-TO-LOAD and STILL-LOADING ARE THREE DIFFERENT FACTS
 * and must render as three different things. That is the whole contract here:
 * this hook makes them impossible to conflate, because `data` stays null until
 * a load actually succeeds and `error` is the only thing that is truthy when one
 * fails.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ──
 * No cache, no dedupe, no polling, no suspense. The dashboard already has a
 * cache layer with a request budget and a semaphore for the analytics screen
 * (dashboard-screen/data/), and a second one here would be two answers to the
 * same question. This is the plain case: one screen, one list, one retry.
 *
 * ── ORDERING ──
 * Responses are matched against a request id, so a slow first answer cannot
 * overwrite a fast second one. That is not theoretical on these screens: every
 * one of them reloads on a filter or a role change.
 *
 * ── USING IT ──
 *   const buildings = useResource(loadBuildings, {
 *     fallbackMessage: 'We could not load your buildings.',
 *   });
 *
 *   if (buildings.isLoading) return <Skeleton />;
 *   if (buildings.error) return <ErrorBanner onRetry={buildings.reload} />;
 *   if (!buildings.data?.length) return <EmptyState />;
 *
 * The order of those three checks is the point. Reversing the last two is the
 * bug this hook was written to delete.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface Resource<T> {
  /** The last SUCCESSFUL answer. Null until one arrives — never [] on failure. */
  data: T | null;
  /** True while a load is in flight and no answer has been shown yet — the
   *  FIRST load only. A reload with data already on screen is `isRefreshing`,
   *  so the screen keeps the table (dimmed) instead of swapping it for a
   *  skeleton and back, which read as the page reloading. */
  isLoading: boolean;
  /** True while a reload is in flight over data that is already showing. */
  isRefreshing: boolean;
  /** A sentence for the reader. Null when the last load succeeded. */
  error: string | null;
  /** Run it again. Safe to pass straight to a Try again button. */
  reload: () => Promise<void>;
  /** For a local mutation that must show immediately (a rename, a removal)
   *  without paying for a round trip. */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export interface ResourceOptions {
  /** Skip the load entirely — for a screen that must wait for a user, a
   *  building or a permission before it can ask anything. Stays `isLoading`
   *  false and `data` null rather than pretending to have asked. */
  enabled?: boolean;
  /** What to say when the error carries nothing readable. Written for the
   *  reader, not the console: name what failed, not the status code. */
  fallbackMessage?: string;
  /** Values that CHANGE WHAT THE REQUEST IS, so the load re-runs when they move.
   *
   *  Not a general dependency array: the loader itself is read through a ref
   *  precisely so an inline arrow does not re-fire on every render. But a screen
   *  whose filter is applied SERVER-SIDE — the People screen's active/inactive
   *  view, which is `?status=inactive` and therefore a different request rather
   *  than a different filter over one payload — has no other way to say "that
   *  changed, ask again". Without it the control moves and the list does not.
   *
   *  Compared by value (JSON), so a caller may pass a fresh array literal each
   *  render without looping. */
  deps?: ReadonlyArray<string | number | boolean | null | undefined>;
}

/** A server error's own sentence, if it has one a person can read. */
const messageFrom = (error: unknown, fallback: string): string => {
  if (typeof error === 'string' && error.trim()) return error;

  if (error && typeof error === 'object') {
    const shaped = error as {
      message?: unknown;
      response?: { data?: { message?: unknown; error?: unknown } };
    };
    const fromBody = shaped.response?.data?.message ?? shaped.response?.data?.error;

    if (typeof fromBody === 'string' && fromBody.trim()) return fromBody;

    // `error.message` LAST, and only when it is not axios boilerplate. "Request
    // failed with status code 500" and "Network Error" are strings about HTTP,
    // not about the user's data, and putting one on screen is the technical
    // leak the audit flagged across this codebase.
    const own = shaped.message;
    if (
      typeof own === 'string' &&
      own.trim() &&
      !/^request failed with status code/i.test(own) &&
      !/^network error$/i.test(own) &&
      !/^timeout of /i.test(own)
    ) {
      return own;
    }
  }

  return fallback;
};

export const useResource = <T,>(
  load: () => Promise<T>,
  options: ResourceOptions = {}
): Resource<T> => {
  const { enabled = true, fallbackMessage = 'We could not load that.', deps } = options;
  // Serialised so the effect below can depend on the VALUES rather than on the
  // array's identity — a caller writing `deps: [lifecycle]` inline creates a new
  // array every render, and depending on that would reload forever.
  const depsKey = JSON.stringify(deps ?? []);

  const [data, setData] = useState<T | null>(null);
  const [inFlight, setInFlight] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const requestRef = useRef(0);
  // The loader is read through a ref so that an inline arrow at the call site
  // does not re-trigger the effect on every render. The caller keeps writing
  // `useResource(() => listThings(id))` and it behaves.
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    const id = requestRef.current + 1;
    requestRef.current = id;

    setInFlight(true);
    setError(null);

    try {
      const answer = await loadRef.current();

      if (requestRef.current !== id) return;
      setData(answer);
    } catch (caught) {
      if (requestRef.current !== id) return;
      // `data` IS LEFT ALONE. A failed refresh of a list the reader is looking
      // at should not blank the screen; it should say the refresh failed. The
      // first load has data === null anyway, so the empty case is unaffected.
      setError(messageFrom(caught, fallbackMessage));
    } finally {
      if (requestRef.current === id) setInFlight(false);
    }
  }, [fallbackMessage]);

  useEffect(() => {
    if (!enabled) {
      setInFlight(false);

      return;
    }

    void reload();
    // eslint-disable-next-line
  }, [enabled, reload, depsKey]);

  const isLoading = inFlight && data === null;
  const isRefreshing = inFlight && data !== null;

  return { data, isLoading, isRefreshing, error, reload, setData };
};
